import { useState, useEffect, useCallback, useRef } from 'react';
import AppShell from '../components/AppShell';
import { centerShipperAPI } from '../api/client';
import { alertDialog, confirmDialog, toast } from '../components/AppDialog';
import { Page, PageHeader, Button, SearchInput } from '../components/ui';
import { COLORS, TONES, RADIUS, SHADOW, input as uiInput, btn as uiBtn, badge } from '../utils/uiStyles';
import { IconBuilding, IconDownload, IconFileSpreadsheet } from '@tabler/icons-react';

/* 센터 고객사 명부 (마스터 2026-08-20, 설계 patch_center_daily_report 단계2)
 * 일별 실적의 뼈대 — 어느 고객사가 자동(2.0)이고 어느 고객사가 수기(1.0)인지,
 * 2.0이면 어느 화주코드·창고인지를 여기서 정한다.
 * 🔴 2.0인데 화주코드가 없으면 자동 집계에서 조용히 0이 된다 → 상단에 미매핑 개수를 크게 띄운다.
 */
const SYS = ['2.0', '1.0'];
const DIVS = ['B2C', 'B2B', '홈쇼핑'];
const ZONES = ['저온', '상온'];

const th = { height: '33px', padding: '5px 9px', textAlign: 'left', fontSize: '12px', fontWeight: 400, color: COLORS.textMuted, whiteSpace: 'nowrap', borderBottom: `1px solid ${COLORS.borderSoft}` };
const td = { height: '41px', padding: '7px 9px', fontSize: '13px', color: COLORS.textSub, borderBottom: `1px solid ${COLORS.borderSoft}`, verticalAlign: 'middle' };
// 각 표의 첫 열 — 값은 th/td 와 같고 왼쪽 패딩만 4(시안 .database :first-child). shorthand 와 paddingLeft 를 한 객체에 섞지 않는다.
const thFirst = { textAlign: 'left', height: '33px', paddingTop: '5px', paddingRight: '9px', paddingBottom: '5px', paddingLeft: '4px', borderBottom: `1px solid ${COLORS.borderSoft}`, color: COLORS.textMuted, fontWeight: 400, fontSize: '12px', whiteSpace: 'nowrap' };
const tdFirst = { height: '41px', paddingTop: '7px', paddingRight: '9px', paddingBottom: '7px', paddingLeft: '4px', fontSize: '13px', color: COLORS.text, fontWeight: 500, borderBottom: `1px solid ${COLORS.borderSoft}`, verticalAlign: 'middle' };
const inp = { ...uiInput };
// 모달 — 시안 .modal-backdrop/.modal(F:24). 판 폭은 모달마다 다르다(미리보기는 표 때문에 760).
const backdrop = { position: 'fixed', inset: 0, background: 'var(--gw-overlay, #19191945)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 80, padding: '20px' };
const modalPanel = { background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.xl, boxShadow: SHADOW.float, padding: '25px', maxWidth: '100%', maxHeight: '86vh', overflowY: 'auto' };
const modalTitle = { fontSize: '20px', fontWeight: 700, color: COLORS.text };

export default function CenterShipperPage() {
  const [centers, setCenters] = useState([]);
  const [center, setCenter] = useState('');
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0, unmapped_count: 0, manual_count: 0 });
  const [q, setQ] = useState('');
  const [onlyUnmapped, setOnlyUnmapped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);      // 편집 중인 행(사본)
  const [pwnQ, setPwnQ] = useState('');
  const [pwnHits, setPwnHits] = useState([]);
  const [preview, setPreview] = useState(null);      // 엑셀 미리보기
  const fileRef = useRef(null);

  useEffect(() => {
    centerShipperAPI.myCenters()
      .then((r) => {
        const cs = r.data?.centers || [];
        setCenters(cs);
        if (cs.length) setCenter((prev) => prev || cs[0].center_key);
        else setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const load = useCallback(async () => {
    if (!center) return;
    setLoading(true);
    try {
      const r = await centerShipperAPI.list(center, { q, only_unmapped: onlyUnmapped });
      setMeta(r.data.center);
      setItems(r.data.items || []);
      setCounts({ total: r.data.total, unmapped_count: r.data.unmapped_count, manual_count: r.data.manual_count });
    } catch (e) {
      if (e.response?.status === 403) alertDialog('이 센터에 접근할 권한이 없습니다.');
      setItems([]);
    } finally { setLoading(false); }
  }, [center, q, onlyUnmapped]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (row) => { setEditing({ ...row }); setPwnQ(''); setPwnHits([]); };

  const save = async () => {
    const body = { ...editing, center_key: center };
    try {
      if (editing.id) await centerShipperAPI.update(editing.id, body);
      else await centerShipperAPI.create(body);
      setEditing(null); toast('저장했습니다.'); load();
    } catch (e) {
      alertDialog('저장 실패: ' + (e.response?.data?.detail || e.message));
    }
  };

  const remove = async (row) => {
    if (!(await confirmDialog(`'${row.display_name}'을(를) 삭제할까요?\n\n실적이 쌓인 뒤 삭제하면 과거 보고서에서 이 고객사가 사라집니다. 더 쓰지 않을 뿐이면 '사용 안 함'으로 두세요.`))) return;
    try { await centerShipperAPI.remove(row.id); load(); }
    catch (e) { alertDialog('삭제 실패: ' + (e.response?.data?.detail || e.message)); }
  };

  const searchPwn = async () => {
    try {
      const r = await centerShipperAPI.pwnCandidates(pwnQ);
      setPwnHits(r.data?.items || []);
    } catch (e) { alertDialog('화주 조회 실패: ' + (e.response?.data?.detail || e.message)); }
  };

  // 🔴 링크로 열면 토큰이 안 실려 'Not authenticated' 가 뜬다(2026-08-20 마스터 캡처) → blob 으로 받는다
  const downloadTemplate = async () => {
    try {
      const r = await centerShipperAPI.template(center);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `고객사명부_업로드양식_${center || '공통'}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { alertDialog('양식 내려받기에 실패했습니다: ' + (e.response?.data?.detail || e.message)); }
  };

  const upload = async (f) => {
    if (!f) return;
    try {
      const r = await centerShipperAPI.importPreview(center, f);
      setPreview(r.data);
    } catch (e) {
      alertDialog('업로드 실패: ' + (e.response?.data?.detail || e.message));
    } finally { if (fileRef.current) fileRef.current.value = ''; }
  };

  const applyImport = async (overwrite) => {
    try {
      const r = await centerShipperAPI.importApply({ center_key: center, items: preview.items, overwrite });
      toast(`신규 ${r.data.created}건 · 갱신 ${r.data.updated}건 · 건너뜀 ${r.data.skipped}건`);
      setPreview(null); load();
    } catch (e) { alertDialog('반영 실패: ' + (e.response?.data?.detail || e.message)); }
  };

  return (
    <AppShell app="center-shippers">
      {/* 🔴 본문은 사이드바의 **형제**다 — AppSidebar 의 children 은 사이드바 아코디언용이라
          거기에 넣으면 본문이 사이드바 안에서 열린다(레포 규약: CenterCostPage·DirectoryPage 참조). */}
      <div style={{ flex: 1, minWidth: 0, height: 'var(--gw-app-h, 100dvh)', overflow: 'auto' }}>
      <Page>
        <PageHeader
          icon={<IconBuilding aria-hidden="true" />}
          title="센터 고객사 명부"
          subtitle={(
            <>
              일별 실적의 집계·입력 단위입니다. 2.0 고객사는 화주코드를 연결하면 출고가 자동 집계되고, 1.0 고객사는 수기 입력 대상이 됩니다.
              <br />
              한 번에 등록하려면 '업로드 양식'을 받아 채운 뒤 올리세요(센터 창고코드가 안내 시트에 들어 있습니다).
              기존 센터 엑셀(일별실적·사업자_마스터 시트가 있는 파일)을 그대로 올려도 읽습니다. 올리면 미리보기가 먼저 뜹니다.
            </>
          )}
        />

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
          <select value={center} onChange={(e) => setCenter(e.target.value)} style={{ ...inp, width: 'auto', minWidth: '160px' }}>
            {centers.map((c) => (
              <option key={c.center_key} value={c.center_key}>
                {c.display_name}{c.temp_zone ? ` (${c.temp_zone})` : ''}{c.center_type === 'consign' ? ' · 위탁' : ''}
              </option>
            ))}
          </select>
          <SearchInput width={240} value={q} onChange={(e) => setQ(e.target.value)} placeholder="고객사·사업자번호·화주코드 검색" />
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: COLORS.text }}>
            <input type="checkbox" checked={onlyUnmapped} onChange={(e) => setOnlyUnmapped(e.target.checked)} /> 매핑 미완만
          </label>
          <div style={{ flex: 1 }} />
          <input ref={fileRef} type="file" accept=".xlsx" onChange={(e) => upload(e.target.files?.[0])} style={{ display: 'none' }} />
          <Button variant="secondary" icon={<IconDownload aria-hidden="true" />} onClick={downloadTemplate}
             title="이 센터의 창고코드·품온이 채워진 서식을 내려받습니다">업로드 양식</Button>
          <Button variant="secondary" icon={<IconFileSpreadsheet aria-hidden="true" />} onClick={() => fileRef.current?.click()}>엑셀 업로드</Button>
          <Button variant="primary" onClick={() => startEdit({ display_name: '', system_ver: '2.0', active: true })}>+ 고객사 추가</Button>
        </div>

        {meta && !meta.has_depot && (
          <div style={{ padding: '8px 12px', background: COLORS.warnSoft, color: COLORS.text, borderRadius: '8px', fontSize: '12px', marginBottom: '10px' }}>
            이 센터는 WMS 창고가 등록되지 않아 자동 집계를 할 수 없습니다 — 전 고객사가 수기 입력 대상입니다.
          </div>
        )}
        {counts.unmapped_count > 0 && (
          <div style={{ padding: '8px 12px', background: COLORS.dangerSoft, color: COLORS.text, borderRadius: '8px', fontSize: '12px', marginBottom: '10px' }}>
            <strong>화주코드 미연결 {counts.unmapped_count}곳</strong> — 2.0 고객사인데 화주코드가 없으면 출고가 0으로 집계됩니다. 연결해 주세요.
          </div>
        )}

        <div style={{ fontSize: '12px', color: COLORS.textSub, marginBottom: '6px' }}>
          전체 {counts.total}곳 · 수기 대상 {counts.manual_count}곳
        </div>

        <div style={{ background: COLORS.surface, borderRadius: RADIUS.lg, overflow: 'auto', border: `1px solid ${COLORS.borderSoft}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '980px' }}>
            <thead><tr>
              {['표시명', '법인명(등록증)', '사업자번호', '시스템', '화주코드', '창고', '품온', '출고구분', '상태', '관리'].map((h, i) => (
                <th key={h} style={i === 0 ? { ...thFirst, position: 'sticky', left: 0, zIndex: 1, background: COLORS.surface } : th}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {loading && <tr><td style={{ ...td, textAlign: 'center', color: COLORS.textMuted }} colSpan={10}>불러오는 중…</td></tr>}
              {!loading && !items.length && <tr><td style={{ ...td, textAlign: 'center', color: COLORS.textMuted }} colSpan={10}>등록된 고객사가 없습니다. 엑셀을 업로드하거나 직접 추가하세요.</td></tr>}
              {!loading && items.map((s) => {
                // 상태색은 tr 이 아니라 각 td 에(hover 에도 유지) · 1.0 = 수기 대상 회색 · 고정 첫 칸은 비치지 않게 불투명 바탕
                const rowBg = s.needs_mapping ? COLORS.dangerSoft : (s.system_ver === '1.0' ? TONES.gray.soft : undefined);
                const cell = { ...td, background: rowBg };
                return (
                  <tr key={s.id} className="gw-tr">
                    <td style={{ ...tdFirst, position: 'sticky', left: 0, zIndex: 1, background: rowBg || COLORS.surface }}>{s.display_name}</td>
                    <td style={cell}>{s.legal_name || '-'}</td>
                    <td style={cell}>{s.biz_no || '-'}</td>
                    <td style={cell}>{s.system_ver}{s.system_ver === '1.0' && <span style={{ fontSize: '11px', color: COLORS.textMuted }}> (수기)</span>}</td>
                    <td style={cell}>{s.pwn_cd || (s.system_ver === '2.0' ? <span style={{ color: COLORS.text }}>미연결</span> : '-')}</td>
                    <td style={cell}>{s.depot_cd || '-'}</td>
                    <td style={cell}>{s.temp_zone || '-'}</td>
                    <td style={cell}>{s.out_div || '-'}</td>
                    <td style={cell}>{s.active ? '사용' : '사용 안 함'}</td>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                      <Button variant="quiet" onClick={() => startEdit(s)} style={{ marginRight: '4px' }}>수정</Button>
                      <Button variant="danger" onClick={() => remove(s)}>삭제</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {editing && (
          <div style={backdrop} onClick={() => setEditing(null)}>
            <div className="gw-dialog" onClick={(e) => e.stopPropagation()} style={{ ...modalPanel, width: '520px' }}>
              <h2 style={{ margin: '0 0 12px', ...modalTitle }}>{editing.id ? '고객사 수정' : '고객사 추가'}</h2>
              {[['display_name', '표시명(보고서에 찍힘)'], ['legal_name', '법인명(사업자등록증)'], ['biz_no', '사업자번호']].map(([k, label]) => (
                <div key={k} style={{ marginBottom: '8px' }}>
                  <div style={{ fontSize: '11px', color: COLORS.textSub, marginBottom: '3px' }}>{label}</div>
                  <input value={editing[k] || ''} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} style={inp} />
                </div>
              ))}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                {[['system_ver', '시스템', SYS], ['out_div', '출고구분', ['', ...DIVS]], ['temp_zone', '품온', ['', ...ZONES]]].map(([k, label, opts]) => (
                  <div key={k} style={{ flex: 1 }}>
                    <div style={{ fontSize: '11px', color: COLORS.textSub, marginBottom: '3px' }}>{label}</div>
                    <select value={editing[k] || ''} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} style={inp}>
                      {opts.map((o) => <option key={o} value={o}>{o || '-'}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              {editing.system_ver === '2.0' && (
                <div style={{ padding: '10px', border: `1px solid ${COLORS.border}`, borderRadius: '8px', marginBottom: '8px' }}>
                  <div style={{ fontSize: '11px', color: COLORS.textSub, marginBottom: '4px' }}>화주코드 {editing.pwn_cd ? `— 현재 ${editing.pwn_cd}` : '— 미연결(자동 집계 불가)'}</div>
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                    <input value={pwnQ} onChange={(e) => setPwnQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchPwn(); } }} placeholder="화주명·코드 검색" style={inp} />
                    <Button variant="secondary" onClick={searchPwn}>검색</Button>
                  </div>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', maxHeight: '90px', overflowY: 'auto' }}>
                    {pwnHits.map((h) => (
                      <span key={h.pwn_cd} onClick={() => setEditing({ ...editing, pwn_cd: h.pwn_cd })}
                        style={{ ...(editing.pwn_cd === h.pwn_cd ? { ...badge('default'), color: TONES.blue.ink } : badge('gray')), cursor: 'pointer', border: 'none' }}>
                        {h.pwn_nm}
                      </span>
                    ))}
                  </div>
                  {!!(meta?.depot_cds || []).length && (
                    <div style={{ marginTop: '8px' }}>
                      <div style={{ fontSize: '11px', color: COLORS.textSub, marginBottom: '3px' }}>집계 창고(저온/상온이 갈리면 반드시 지정)</div>
                      <select value={editing.depot_cd || ''} onChange={(e) => setEditing({ ...editing, depot_cd: e.target.value })} style={inp}>
                        <option value="">센터 전체</option>
                        {(meta.depot_cds || []).map((d) => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', marginBottom: '12px' }}>
                <input type="checkbox" checked={editing.active !== false} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> 사용
              </label>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <Button variant="secondary" onClick={() => setEditing(null)}>취소</Button>
                <Button variant="primary" onClick={save}>저장</Button>
              </div>
            </div>
          </div>
        )}

        {preview && (
          <div style={backdrop} onClick={() => setPreview(null)}>
            {/* 미리보기 판은 표(6열·최대 100행) 때문에 폭 760 유지 — 시안 520 과 일부러 다르다 */}
            <div className="gw-dialog" onClick={(e) => e.stopPropagation()} style={{ ...modalPanel, width: '760px' }}>
              <h2 style={{ margin: '0 0 6px', ...modalTitle }}>엑셀 업로드 미리보기</h2>
              <div style={{ fontSize: '12px', color: COLORS.textSub, marginBottom: '10px' }}>
                총 {preview.total}건 — 신규 {preview.new_count} · 이미 등록됨 {preview.exist_count}. 확인 후 반영하세요.
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>{['표시명', '법인명', '사업자번호', '구분', '품온', '상태'].map((h, i) => <th key={h} style={i === 0 ? thFirst : th}>{h}</th>)}</tr></thead>
                <tbody>
                  {preview.items.slice(0, 100).map((it, i) => (
                    <tr key={i} className="gw-tr">
                      <td style={tdFirst}>{it.display_name}</td>
                      <td style={td}>{it.legal_name || '-'}</td>
                      <td style={td}>{it.biz_no || '-'}</td>
                      <td style={td}>{it.out_div || '-'}</td>
                      <td style={td}>{it.temp_zone || '-'}</td>
                      <td style={{ ...td, color: it.exists ? COLORS.textMuted : COLORS.successText }}>{it.exists ? '기존' : '신규'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.items.length > 100 && <div style={{ fontSize: '11px', color: COLORS.textMuted, padding: '6px' }}>…앞 100건만 표시(반영은 전체)</div>}
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' }}>
                <Button variant="secondary" onClick={() => setPreview(null)}>취소</Button>
                <Button variant="primary" onClick={() => applyImport(false)}>신규만 추가</Button>
                {/* 시안 밖 채움색 — Button 의 primary hover(!important)가 바탕을 덮으므로 gw-btn 만 달고 직접 그린다 */}
                <button type="button" className="gw-btn" onClick={() => applyImport(true)} style={{ ...uiBtn('primary'), background: COLORS.warn }}>기존도 덮어쓰기</button>
              </div>
            </div>
          </div>
        )}
      </Page>
      </div>
    </AppShell>
  );
}
